'use client'

import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react'

export type AuthView = 'login' | 'join'

type AuthModalConfig = {
    next?: string
    onSuccess?: (() => void | Promise<void>) | undefined
    view?: AuthView
}

type AuthModalState = {
    next?: string
    onSuccess?: (() => void | Promise<void>) | undefined
    view: AuthView
}

type AuthModalContextValue = {
    closeAuthModal: () => void
    completeAuth: () => Promise<void>
    isOpen: boolean
    next?: string
    openAuthModal: (config?: AuthModalConfig) => void
    setView: (view: AuthView) => void
    view: AuthView
}

const noopAsync = async () => {}

const AuthModalContext = createContext<AuthModalContextValue>({
    closeAuthModal: () => {},
    completeAuth: noopAsync,
    isOpen: false,
    next: undefined,
    openAuthModal: () => {},
    setView: () => {},
    view: 'login',
})

export const useAuthModal = () => useContext(AuthModalContext)

export const AuthModalContextManager = ({ children }: { children: ReactNode }) => {
    const [modalState, setModalState] = useState<AuthModalState | null>(null)

    const closeAuthModal = useCallback(() => {
        setModalState(null)
    }, [])

    const completeAuth = useCallback(async () => {
        const onSuccess = modalState?.onSuccess
        setModalState(null)
        await onSuccess?.()
    }, [modalState])

    const openAuthModal = useCallback((config: AuthModalConfig = {}) => {
        setModalState({
            next: config.next,
            onSuccess: config.onSuccess,
            view: config.view || 'login',
        })
    }, [])

    const setView = useCallback((view: AuthView) => {
        setModalState((currentState) => (currentState ? { ...currentState, view } : currentState))
    }, [])

    const value = useMemo<AuthModalContextValue>(
        () => ({
            closeAuthModal,
            completeAuth,
            isOpen: !!modalState,
            next: modalState?.next,
            openAuthModal,
            setView,
            view: modalState?.view || 'login',
        }),
        [closeAuthModal, completeAuth, modalState, openAuthModal, setView]
    )

    return <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>
}
